import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, categories } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';
import { useState, useMemo } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || '');
  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const categoryArticles = useMemo(() => {
    if (!activeCategory) return [];
    return articles.filter((a) => a.categorySlug === activeCategory);
  }, [activeCategory]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    window.history.pushState(null, '', `/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  if (!currentCategory) {
    return <NotFound />;
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
          <img
            src={currentCategory.image}
            alt={currentCategory.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="max-w-7xl mx-auto">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-2">
                {currentCategory.name}
              </h1>
              <p className="text-white/70 text-sm md:text-base max-w-2xl">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </section>

        <section className="py-6 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>
        </section>

        <section className="pb-6 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 text-sm rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>

        <section className="py-10 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">
              其他分类推荐
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {categories
                .filter((c) => c.slug !== activeCategory)
                .map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer text-center"
                  >
                    <div className="w-10 h-10 flex items-center justify-center">
                      <i className={`${cat.icon} text-xl text-primary-500`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800">
                      {cat.name}
                    </span>
                    <span className="text-xs text-foreground-500">
                      {cat.articleCount} 篇
                    </span>
                  </button>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory.name} - 港片影志`,
            description: currentCategory.description,
            url: `https://pczqmcf.cn/category/${currentCategory.slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '港片影志',
            },
          }),
        }}
      />
    </>
  );
}